import { CloseButton, TabPanel, Tabs } from 'components';
import { ROUTES } from 'configs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { doRequestCurrentCommunity } from 'store/actionCreators/currentCommunity';
import { getCommunities } from 'store/selectors/communities';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
  getIsMenuOpen,
} from 'store/selectors/currentCommunity';
import { CommunityRoles } from 'types';

import EventTypesTab from './EventTypes';
import GeneralTab from './General';
import Roles from './Members';
import NotificationsTab from './NotificationsTab';
import Other from './Other';
import Requests from './Requests';
import ResponsesTab from './Responses';
import {
  StyledContentWrapper,
  StyledHeader,
  StyledTitle,
  TabWrapper,
} from './styled';
import TasksTab from './Tasks';

const COMMUNITY_SETTINGS_HASH_TAB_MAP: Record<string, number> = {
  '#notifications': 1,
  '#event-types': 2,
  '#members': 5,
  '#requests': 6,
  '#other': 7,
};

const CommunitySettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const community = useSelector(getCurrentCommunity);
  const communities = useSelector(getCommunities);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const { communityId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!community) {
      dispatch(doRequestCurrentCommunity({ communityId }));
    }
  }, [doRequestCurrentCommunity, communityId, community]);

  const userRole = currentUser?.communityPermission?.role;
  const canManageCommunity =
    userRole === CommunityRoles.ADMIN || userRole === CommunityRoles.OWNER;
  const invitesCount =
    communities.find((c) => c.id === community?.id)?.requests?.length || 0;

  useEffect(() => {
    if (!community?.id || !currentUser || canManageCommunity) {
      return;
    }

    navigate(ROUTES.PAGE404);
  }, [canManageCommunity, community?.id, currentUser, navigate]);

  const goBack = () => {
    navigate(-1);
  };

  if (!community?.id || !currentUser || !canManageCommunity) {
    return null;
  }

  return (
    <StyledContentWrapper isMenuOpen={isMenuOpen}>
      {/*<Prompt when={edit} message={t('common.modalExit')} />*/}
      <StyledHeader isMenuOpen={isMenuOpen}>
        <StyledTitle isMenuOpen={isMenuOpen}>
          {t('communitySettings.title')}
        </StyledTitle>
        <CloseButton hidden={false} onClick={goBack} />
      </StyledHeader>
      <Tabs
        variant="navigation"
        navigationLayout="panel"
        invitesCount={invitesCount}
        hashTabMap={COMMUNITY_SETTINGS_HASH_TAB_MAP}
      >
        <TabPanel label={t('communitySettings.tabs.general')} index={0}>
          <TabWrapper>
            <GeneralTab />
          </TabWrapper>
        </TabPanel>
        <TabPanel label={t('common.notifications')} index={1}>
          <TabWrapper>
            <NotificationsTab />
          </TabWrapper>
        </TabPanel>
        <TabPanel label={t('communitySettings.tabs.eventTypes')} index={2}>
          <EventTypesTab />
        </TabPanel>
        <TabPanel label={t('communitySettings.tabs.responses')} index={3}>
          <ResponsesTab />
        </TabPanel>
        <TabPanel label={t('communitySettings.tabs.tasks')} index={4}>
          <TasksTab />
        </TabPanel>
        <TabPanel label={t('communitySettings.tabs.roles')} index={5}>
          <Roles />
        </TabPanel>
        <TabPanel
          tabId="request"
          label={t('communitySettings.tabs.requests')}
          index={6}
        >
          <Requests />
        </TabPanel>
        {currentUser.communityPermission?.role === CommunityRoles.OWNER ? (
          <TabPanel label={t('communitySettings.tabs.other')} index={7}>
            <Other />
          </TabPanel>
        ) : null}
      </Tabs>
    </StyledContentWrapper>
  );
};

export default CommunitySettingsPage;
